'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';

interface ForgotPasswordForm {
  email: string;
}

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = React.useState(false);
  const { register, handleSubmit, getValues, formState: { errors, isSubmitting } } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    const res = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: data.email }),
    });
    const json = await res.json();
    if (!res.ok) {
      toast.error(json.message ?? 'Something went wrong');
      return;
    }
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <AppLogo variant="light" height={36} />
        </div>
        <div>
          <h1 className="text-display text-white mb-4 leading-tight">
            Forgot your<br />password?
          </h1>
          <p className="text-white/60 text-base max-w-sm">
            No problem. Enter your email and we'll send you a secure link to reset it.
          </p>
        </div>
        <p className="text-white/30 text-xs">© 2026 KlugCMMS. Accounts are managed by your organization administrator.</p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo variant="dark" height={32} />
          </div>

          {!submitted ? (
            <>
              <div className="mb-8">
                <h2 className="text-title-lg text-foreground">Reset your password</h2>
                <p className="text-body-md text-muted-foreground mt-1">
                  Enter the email address linked to your account and we'll send a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="email" className="block text-label-lg text-foreground mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@organization.com"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.email ? 'border-error' : 'border-border'}`}
                    {...register('email', {
                      required: 'Email address is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                  {errors.email && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <AlertCircle size={13} className="text-error flex-shrink-0" />
                      <p className="text-xs text-error">{errors.email.message}</p>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-3 text-base">
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send Reset Link'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-success/10 flex items-center justify-center mx-auto mb-6">
                <Mail size={28} className="text-success" />
              </div>
              <h2 className="text-title-lg text-foreground mb-2">Check your inbox</h2>
              <p className="text-body-md text-muted-foreground mb-2">
                We sent a password reset link to
              </p>
              <p className="text-sm font-semibold text-foreground font-tabular mb-6">{getValues('email')}</p>
              <p className="text-body-sm text-muted-foreground mb-8">
                The link expires in 1 hour. If you don't see it, check your spam folder.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="btn-outlined w-full mb-3"
              >
                Try a different email
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link href="/sign-up-login" className="inline-flex items-center gap-1.5 text-body-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={14} />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
